import { useMemo } from 'react';

import { getActiveServiceTypes } from '@/lib/jobs/appointmentFormModel';
import type { SchedulerTechnician, ServiceType } from '@/types/scheduler';
import { useSchedulerTechnicians } from './useSchedulerTechnicians';
import { useServiceTypes } from './useServiceTypes';

type TechnicianExpertise = {
  serviceTypeId?: string | null;
  serviceType?: Pick<ServiceType, 'id'> | null;
};

type SchedulerTechnicianWithExpertise = SchedulerTechnician & {
  expertise?: TechnicianExpertise[] | null;
};

function getTechnicianExpertiseIds(technician: SchedulerTechnicianWithExpertise | undefined) {
  return new Set(
    (technician?.expertise ?? [])
      .map((item) => item.serviceTypeId ?? item.serviceType?.id)
      .filter((id): id is string => Boolean(id)),
  );
}

export function useTechnicianAllowedServiceTypes(technicianId: string | undefined) {
  const techniciansQuery = useSchedulerTechnicians();
  const serviceTypesQuery = useServiceTypes();

  return useMemo(() => {
    if (!technicianId) return undefined;
    if (techniciansQuery.isLoading || serviceTypesQuery.isLoading) return undefined;

    const technician = (techniciansQuery.data ?? []).find((item) => item.id === technicianId) as
      | SchedulerTechnicianWithExpertise
      | undefined;

    if (!technician) return undefined;

    const expertiseIds = getTechnicianExpertiseIds(technician);
    if (expertiseIds.size === 0) return [];

    return getActiveServiceTypes(serviceTypesQuery.data).filter((serviceType) =>
      expertiseIds.has(serviceType.id),
    );
  }, [
    serviceTypesQuery.data,
    serviceTypesQuery.isLoading,
    technicianId,
    techniciansQuery.data,
    techniciansQuery.isLoading,
  ]);
}
